// src/share/StoryViewer.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  decodeAlbumPayload,
  isStoredShareHash,
  loadStoredAlbumFromHash,
  getShareBaseUrl,  // ← FIX: Import for Android localhost fix
} from "../utils/share-v2";
import StoryPlayer from "../StoryPlayer";
import { RenderPage } from "../components/FlipbookPreview"; // ← ADDED for proper page rendering
import { waitForAuthReady } from '../firebaseClient';
import "./StoryViewer.css";



// Component to properly scale album pages for mobile
function FlipbookPageWrapper({ page, photoIndex }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScale = () => {
      const containerWidth = container.clientWidth;
      const newScale = containerWidth / 1024;
      setScale(newScale > 0 ? newScale : 1);
    };

    updateScale();
    
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);
    
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef}
      className="mx-auto bg-white rounded-xl shadow-lg overflow-hidden"
      style={{
        width: '100%',
        maxWidth: '1024px',
        aspectRatio: '1024 / 680',
        position: 'relative',
      }}
    >
      <div 
        style={{
          width: '1024px',
          height: '680px',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <RenderPage
          page={page}
          photoIndex={photoIndex}
          isPrinting={false}
        />
      </div>
    </div>
  );
}

// Build a lookup from photoId -> photo object
function buildPhotoIndex(photos) {
  if (!photos) return {};
  if (Array.isArray(photos)) {
    const entries = photos
      .filter(Boolean)
      .map((ph) => {
        const id = ph.id || ph.photoId || ph.url || ph.src;
        const url = ph.url || ph.src;
        return [id, { ...ph, url, id }];
      })
      .filter(([id, photo]) => !!id && !!photo.url);

    const index = Object.fromEntries(entries);
    console.log(
      "[buildPhotoIndex] Built index with",
      Object.keys(index).length,
      "photos"
    );
    return index;
  }
  if (typeof photos === "object") {
    return photos;
  }
  return {};
}

/**
 * Transform Vivida album format to Story Player format.
 */
function transformToStoryFormat(payload) {
  if (!payload) return null;

  const { trip, pages, photos } = payload;

  console.log("[transformToStoryFormat] Input:", {
    pagesCount: pages?.length,
    photosCount: Array.isArray(photos) ? photos.length : typeof photos,
    hasPhotos: !!photos,
  });

  let photoIndex = buildPhotoIndex(photos);

  if (Object.keys(photoIndex).length === 0) {
    console.log(
      "[transformToStoryFormat] photoIndex empty, building from page items..."
    );
    (pages || []).forEach((page) => {
      if (Array.isArray(page.items)) {
        page.items.forEach((item) => {
          if (item.url || item.src) {
            const url = item.url || item.src;
            const id = item.photoId || item.id || url;
            photoIndex[id] = {
              id,
              url,
              position: item.position || { x: 50, y: 50, scale: 100 },
              ...item,
            };
          }
        });
      }
      if (Array.isArray(page.photos)) {
        page.photos.forEach((ph) => {
          const url = ph.src || ph.url;
          const id = ph.id || ph.photoId || url;
          if (url && !photoIndex[id]) {
            photoIndex[id] = {
              id,
              url,
              position: ph.position || { x: 50, y: 50, scale: 100 },
              ...ph,
            };
          }
        });
      }
    });
  }

  console.log(
    "[transformToStoryFormat] Final photoIndex:",
    Object.keys(photoIndex).length,
    "photos"
  );

  const storyPages = (pages || []).map((page, index) => {
    if (index === 0) {
      console.log("[transformToStoryFormat] First page structure:", {
        hasItems: Array.isArray(page.items),
        itemsCount: page.items?.length,
        firstItem: page.items?.[0],
        hasPhotos: Array.isArray(page.photos),
        photosCount: page.photos?.length,
        templateKey: page.templateKey || page.type,
      });
    }

    let photoUrls = [];

    if (Array.isArray(page.photos)) {
      photoUrls = page.photos
        .map((ph) => ph.src || ph.url)
        .filter(Boolean);
    } else if (Array.isArray(page.items)) {
      photoUrls = page.items
        .map((item) => item.url || item.src)
        .filter(Boolean);
    }

    const caption =
      page.caption ||
      page.meta?.caption ||
      page.meta?.title ||
      page.meta?.text ||
      "";

    const journalText =
      page.description ||
      page.journalText ||
      page.meta?.description ||
      "";

    const location =
      page.location || page.meta?.location || page.title || "";

    return {
      photos: photoUrls,
      imageUrl: photoUrls[0],
      location,
      title: page.title || `Page ${index + 1}`,
      caption,
      journalText,
      type: page.type || page.templateKey || "standard",

      albumPage: page,
      albumPagePhotoIndex: photoIndex,
    };
  });

  return {
    title: trip?.name || "Travel Story",
    tripDates: trip?.dates || "",
    tripRoute: trip?.route || "",
    pages: storyPages,
  };
}

export default function StoryViewer() {
  const [viewMode, setViewMode] = useState("story"); // 'story' or 'flipbook'
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(null); // ← ADD THIS LINE

  // Load payload on mount: inline OR encrypted storage
  // Load payload on mount: inline OR encrypted storage
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const rawHash = window.location.hash?.replace(/^#/, "") || "";
        console.log("[StoryViewer] 🔍 Raw hash:", rawHash?.substring(0, 50) + "...");
        
        if (!rawHash) {
          console.log("[StoryViewer] ❌ No hash found in URL");
          if (!cancelled) {
            setPayload(null);
            setLoading(false);
          }
          return;
        }

        // Check for view mode parameter
        const urlParams = new URLSearchParams(rawHash.split('&').slice(1).join('&'));
        const viewParam = urlParams.get('view');
        if (viewParam === 'flipbook') {
          setViewMode('flipbook');
        }

        // Extract just the hash part (before any &view=)
        const hashPart = rawHash.split('&')[0];

        if (isStoredShareHash(hashPart)) {
          console.log("[StoryViewer] 📦 Detected stored share hash, loading from Firebase...");

          // Android fix: ensure Firebase Auth is ready before fetching Storage
 await waitForAuthReady();

 const withTimeout = (promise, ms = 20000, label = "load-story") =>
   Promise.race([
     promise,
     new Promise((_, rej) =>
       setTimeout(() => rej(new Error(`${label}-timeout`)), ms)
     ),
   ]);
          
          // ← NEW: Add progress callback
          const storedPayload = await withTimeout(
   loadStoredAlbumFromHash(hashPart, (progress) => {


            if (!cancelled) {
              setLoadingProgress(progress); // ← NEW: Update progress
            }
          }),
   20000,
   "loadStoredAlbumFromHash"
 );
          
          console.log("[StoryViewer] ✅ Successfully loaded from Firebase");
          
          if (!cancelled) {
            setPayload(storedPayload);
            setLoadingProgress(null); // ← NEW: Clear progress
            setLoading(false);
          }
          return;
        }

        // Fallback to inline decode for small albums
        console.log("[StoryViewer] 🔍 Attempting inline decode...");
        const inlinePayload = decodeAlbumPayload(hashPart);
        console.log("[StoryViewer] Inline decode result:", inlinePayload ? "Success" : "Failed");
        
        if (!cancelled) {
          setPayload(inlinePayload);
          setLoading(false);
        }
      } catch (err) {
        console.error("[StoryViewer] ❌ Failed to load story payload:", err);
        if (!cancelled) {
          setLoadError(err);
          setLoading(false);
          setLoadingProgress(null); // ← NEW: Clear progress on error
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const storyAlbum = useMemo(() => {
    return transformToStoryFormat(payload);
  }, [payload]);


  const handleShareCurrentStory = async () => {
    try {
      const hashPart = typeof window !== 'undefined' ? (window.location.hash || '') : '';
      // ✅ FIX: Use getShareBaseUrl() instead of window.location.origin (fixes Android localhost issue)
      const baseUrl = getShareBaseUrl();
      const link = `${baseUrl}/story${hashPart || ''}`;
      const title = storyAlbum?.title || 'My Travel Story';

      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title, url: link });
          console.log('[StoryViewer] Shared story via navigator.share');
          return;
        } catch (err) {
          if (err?.name === 'AbortError') {
            console.log('[StoryViewer] User cancelled share');
            return;
          }
          console.warn('[StoryViewer] navigator.share failed, falling back to clipboard:', err);
        }
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        alert('Link copied to clipboard');
      } else if (link) {
        window.prompt('Copy this link:', link);
      }
    } catch (err) {
      console.error('[StoryViewer] Share failed:', err);
      alert('Could not share this story. You can still copy the link from the address bar.');
    }
  };

  


  // Build photoIndex for flipbook view (must be at top level for hooks rules)
  const photoIndex = useMemo(() => {
    if (!payload?.photos) return {};
    return buildPhotoIndex(payload.photos);
  }, [payload?.photos]);

  if (loading) {
    const getLoadingMessage = () => {
      if (!loadingProgress) {
        return {
          title: "Loading story…",
          subtitle: "Preparing your travel story."
        };
      }
      
      if (loadingProgress.phase === 'structure') {
        return {
          title: "Loading album structure…",
          subtitle: "Fetching pages and layout."
        };
      } else if (loadingProgress.phase === 'photos') {
        const { loaded = 0, total = 0 } = loadingProgress;
        return {
          title: `Loading photos: ${loaded}/${total}`,
          subtitle: total > 0 ? `${Math.round((loaded / total) * 100)}% complete` : "Processing images."
        };
      } else if (loadingProgress.phase === 'complete') {
        return {
          title: "Almost ready…",
          subtitle: "Finalizing your story."
        };
      }
      
      return {
        title: "Loading story…",
        subtitle: "Decrypting and preparing your travel story."
      };
    };

    const { title, subtitle } = getLoadingMessage();
    const progress = loadingProgress?.phase === 'photos' && loadingProgress.total > 0
      ? Math.round((loadingProgress.loaded / loadingProgress.total) * 100)
      : 0;

    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          {/* Animated Icon */}
          <div className="mb-6 relative">
            <div className="w-20 h-20 mx-auto">
              <svg className="animate-spin text-blue-500" viewBox="0 0 24 24">
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4" 
                  fill="none" 
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
                />
              </svg>
            </div>
            
            {/* Progress percentage in center */}
            {progress > 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-white">{progress}%</span>
              </div>
            )}
          </div>
          
          {/* Title */}
          <p className="text-lg font-semibold mb-2">{title}</p>
          
          {/* Subtitle */}
          <p className="text-slate-400 text-sm mb-6">{subtitle}</p>
          
          {/* Progress Bar */}
          {progress > 0 && (
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          
          {/* Details for large albums */}
          {loadingProgress?.phase === 'photos' && loadingProgress.total > 20 && (
            <p className="text-xs text-slate-500 mt-4">
              Large album detected. Photos are loading progressively for best performance.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (loadError || !payload || !storyAlbum) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Story Link</h1>
          <p className="text-slate-400 mb-4">
            This story link appears to be invalid, expired, or corrupted.
          </p>
          {loadError && (
            <details className="text-left text-sm text-slate-500 bg-slate-800 p-4 rounded-lg">
              <summary className="cursor-pointer">Technical Details</summary>
              <pre className="mt-2 overflow-auto">{loadError.message}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  // Flipbook view
  if (viewMode === "flipbook") {
    const { trip, pages, photos } = payload;
    const tripName = trip?.name || "Shared Album";
    const tripDates = trip?.dates || "";
    const tripRoute = trip?.route || "";

    console.log('[Flipbook View] Rendering', pages?.length, 'pages with', Object.keys(photoIndex).length, 'photos');

        return (
      <div className="min-h-screen bg-slate-900 text-white">
        {/* Top-right controls */}
        <div className="story-viewer-controls flex flex-col gap-2 items-end">


          <button
            onClick={() => setViewMode("story")}
            className="bg-gradient-to-r from-sky-400 to-blue-500 text-slate-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <span>📖</span>
            <span>View as Story</span>
          </button>
          {/* Share button removed - sharing should happen from main album page
              where subscription logic and Firebase upload are properly wired.
              Uncomment to restore:
          <button
            onClick={handleShareCurrentStory}
            className="bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:bg-black/60 flex items-center gap-2"
          >
            <span>🔗</span>
            <span>Share</span>
          </button>
          */}
        </div>

        {/* Header */}
        <div className="story-viewer-sticky-header bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 sticky top-0 z-10">

          <div className="max-w-7xl mx-auto px-6 py-8">
            <h1 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
              {tripName}
            </h1>
            {tripDates && (
              <p className="text-slate-400 text-base flex items-center gap-2">
                <span>📅</span>
                {tripDates}
              </p>
            )}
            
            <p className="text-slate-500 text-xs mt-2">
              {pages?.length || 0} {pages?.length === 1 ? 'page' : 'pages'}
            </p>
          </div>
        </div>

        {/* Album Pages */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="space-y-10">
            {pages?.map((page, i) => {
              const pageType = page.type || page.templateKey || 'standard';

              let photoCount = 0;
              if (Array.isArray(page.photos)) {
                photoCount = page.photos.length;
              } else if (Array.isArray(page.items)) {
                photoCount = page.items.filter(item => item.url || item.src).length;
              }

              return (
                <div 
                  key={i} 
                  className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-700"
                >
                  {/* Page Header */}
                  <div className="p-6 sm:p-8 border-b border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-white">
                            Page {i + 1}
                          </h2>
                          {pageType !== 'standard' && (
                            <span className="text-xs text-slate-400 mt-1 inline-block capitalize">
                              {pageType.replace('-', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                      {photoCount > 0 && (
                        <span className="text-sm text-slate-400 flex items-center gap-2">
                          <span>📸</span>
                          <span>{photoCount} {photoCount === 1 ? 'photo' : 'photos'}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Render Actual Album Page */}
                  <div className="p-4 sm:p-6 bg-neutral-100">
                    <FlipbookPageWrapper page={page} photoIndex={photoIndex} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-16 text-center pb-8">
            <div className="inline-block bg-slate-800 rounded-2xl px-8 py-6 border border-slate-700">
              <p className="text-slate-400 text-sm mb-2">Shared via</p>
              <p className="text-white text-lg font-semibold">
                🗺️ VivaJour
              </p>
            </div>
          </div>
        </div>
      </div>
    );

  }

  // Story view (default)
    return (
    <div className="story-viewer-wrapper">
      <div className="story-viewer-controls flex flex-col gap-2 items-end">

        <button
          onClick={() => setViewMode("flipbook")}
          className="bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:bg-black/60 flex items-center gap-2"
        >
          <span>📚</span>
          <span>View Flipbook</span>
        </button>
        {/* Share button removed - sharing should happen from main album page
            where subscription logic and Firebase upload are properly wired.
            Uncomment to restore:
        <button
          onClick={handleShareCurrentStory}
          className="bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:bg-black/60 flex items-center gap-2"
        >
          <span>🔗</span>
          <span>Share</span>
        </button>
        */}
      </div>

      <StoryPlayer 
        album={storyAlbum} 
        onClose={() => setViewMode("flipbook")}
      />
    </div>
  );

}